import { useCallback } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { useInput } from '../../../hooks';

export const useSearch = <Item extends Record<string, unknown>, Column extends keyof Item>({
	data,
	searchColumns,
}: {
	data: Item[];
	searchColumns?: Column[];
}) => {
	const searchInput = useInput('');
	const [debouncedData, setDebouncedData] = useDebounceValue(data, 300);

	const onChangeSearch = useCallback(
		(searchTerm: string) => {
			searchInput.onChangeText(searchTerm);
			setDebouncedData(getFilterData(data, searchTerm, searchColumns));
		},
		[data, searchColumns, searchInput, setDebouncedData],
	);

	return {
		searchInput: {
			...searchInput,
			onChangeText: onChangeSearch,
		},
		filteredData: debouncedData,
	};
};

const getFilterData = <Item extends Record<string, unknown>, Column extends keyof Item>(
	data: Item[],
	searchTerm: string,
	searchColumns?: Column[],
) =>
	searchColumns
		? data.filter((item) => {
				return searchColumns.some((column) => {
					const value = item[column];
					if (typeof value !== 'string') {
						return false;
					}

					return value.toLowerCase().includes(searchTerm.toLowerCase());
				});
			})
		: data;
